import { useSettings } from "@/context/AppContext";
import SEO from "@/components/SEO";
import { PageHero, Reveal, SectionHead, Eyebrow } from "@/components/Motion";
import { MapPin, Mail, Phone } from "lucide-react";

export default function About() {
  const { settings } = useSettings();
  if (!settings) return <div className="min-h-screen bg-parchment" />;
  const { about, business: biz, capabilities, founder } = settings;
  const seo = settings.seo?.pages?.about;

  return (
    <div data-testid="about-page">
      <SEO title={seo?.title} description={seo?.description} path="/about" />
      <PageHero eyebrow="About Us" title={about?.headline || "Connecting Indian Quality With Global Markets"} description={settings.business?.tagline} image={settings.hero?.image} />

      <section className="bg-parchment py-20 lg:py-28">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 grid gap-16 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Who We Are</Eyebrow>
            <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-forest leading-tight">{biz?.public_name}</h2>
            <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-gold-dark">{biz?.legal_name}</p>
            <p className="mt-6 text-base leading-relaxed text-[#4A554D]" data-testid="about-who-we-are">{about?.who_we_are}</p>
            <div className="mt-8 border border-forest/10 bg-white p-6 space-y-4">
              <p className="flex items-start gap-3 text-sm text-[#4A554D]"><MapPin className="h-4 w-4 mt-0.5 shrink-0 text-gold-dark" /> {biz?.address}</p>
              {(biz?.phones || []).map((p) => (
                <a key={p.tel} href={`tel:${p.tel}`} className="flex items-center gap-3 text-sm text-forest hover:text-gold-dark transition-colors">
                  <Phone className="h-4 w-4 shrink-0 text-gold-dark" /> {p.display}
                </a>
              ))}
              <a href={`mailto:${biz?.email}`} className="flex items-center gap-3 text-sm text-forest hover:text-gold-dark transition-colors">
                <Mail className="h-4 w-4 shrink-0 text-gold-dark" /> {biz?.email}
              </a>
            </div>
          </Reveal>
          <div className="space-y-6">
            <Reveal delay={0.1}>
              <div className="border-l-2 border-gold bg-white p-8">
                <p className="eyebrow">Our Mission</p>
                <p className="mt-4 font-serif text-2xl text-forest leading-snug" data-testid="about-mission">{about?.mission}</p>
              </div>
            </Reveal>
            <Reveal delay={0.18}>
              <div className="border-l-2 border-gold bg-white p-8">
                <p className="eyebrow">Our Vision</p>
                <p className="mt-4 font-serif text-2xl text-forest leading-snug" data-testid="about-vision">{about?.vision}</p>
              </div>
            </Reveal>
            <Reveal delay={0.26}>
              <div className="bg-forest p-8 grain relative">
                <p className="eyebrow">Our Values</p>
                <div className="mt-5 flex flex-wrap gap-2.5" data-testid="about-values">
                  {(about?.values || []).map((v) => (
                    <span key={v} className="border border-gold/30 px-4 py-2 text-xs font-mono uppercase tracking-[0.16em] text-parchment/85">{v}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-28" data-testid="founder-section">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16 grid gap-14 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] lg:items-start">
          <Reveal>
            <Eyebrow>About the Founder</Eyebrow>
            <div className="mt-8 flex flex-col sm:flex-row gap-8 sm:items-start">
              <div className="relative h-40 w-40 shrink-0 overflow-hidden border border-gold/40 bg-forest" data-testid="founder-photo">
                {founder?.photo ? (
                  <img src={founder.photo} alt={founder.name} className="h-full w-full object-cover" />
                ) : (
                  <span className="flex h-full w-full items-center justify-center font-serif text-5xl text-gold-light">
                    {(founder?.name || "A").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                  </span>
                )}
                <span className="absolute -bottom-px -right-px h-6 w-6 border-b-2 border-r-2 border-gold" />
              </div>
              <div>
                <h2 className="font-serif text-3xl sm:text-4xl text-forest leading-tight" data-testid="founder-name">{founder?.name}</h2>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-gold-dark" data-testid="founder-title">{founder?.title}</p>
                {founder?.intro && <p className="mt-6 text-base leading-relaxed text-[#4A554D]" data-testid="founder-intro">{founder.intro}</p>}
                {founder?.bio && <p className="mt-4 text-base leading-relaxed text-[#4A554D]" data-testid="founder-bio">{founder.bio}</p>}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="border border-forest/10 bg-parchment p-8" data-testid="business-information">
              <Eyebrow>Business Information</Eyebrow>
              <dl className="mt-6 divide-y divide-forest/10">
                {[
                  ["Business Name", biz?.trade_name || biz?.public_name],
                  ["Legal Entity", biz?.legal_name],
                  ["IEC", biz?.iec],
                  ["GSTIN", biz?.gstin],
                  ["Location", biz?.location],
                  ["Operations", biz?.operations],
                  ["Email", biz?.email],
                ].filter(([, v]) => v).map(([k, v]) => (
                  <div key={k} className="grid grid-cols-[120px_1fr] gap-4 py-3.5" data-testid={`business-info-${k.toLowerCase().replace(/\s+/g, "-")}`}>
                    <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-forest/55 pt-0.5">{k}</dt>
                    <dd className={`text-sm text-forest ${k === "IEC" || k === "GSTIN" ? "font-mono tracking-wide" : ""}`}>
                      {k === "Email" ? <a href={`mailto:${v}`} className="hover:text-gold-dark transition-colors">{v}</a> : v}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-forest py-20 lg:py-28 grain relative" data-testid="capabilities-section">
        <div className="mx-auto max-w-[1440px] px-4 sm:px-8 lg:px-16">
          <SectionHead dark eyebrow="Company Capabilities" title="What we handle for you." />
          <div className="mt-14 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {(capabilities || []).map((c, i) => (
              <Reveal key={c.title} delay={(i % 4) * 0.08}>
                <div className="h-full bg-forest p-7 transition-colors duration-500 hover:bg-forest-mid" data-testid={`capability-${i + 1}`}>
                  <span className="font-mono text-xs text-gold/60">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 font-serif text-xl text-parchment">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#9EB8A8]">{c.description}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
